import React, { useCallback, useMemo } from 'react';
import { I18n, Translate } from 'react-redux-i18n';
import { Modal, Button, Form, Message } from 'semantic-ui-react';
import get from 'lodash.get';

import {
  handleValueChange,
  updateRecordPassword,
  newPasswordChange,
  confirmPasswordChange,
  clearRecordChangePasswordMessages,
  verificationModalClear
} from '../../pages/accounts/settings/reducer';
import { logOut, pingSession } from '../../modules/session';
import { getPasswordErrorMessage } from '../../helpers/validationHelper';
import { getPolicyFromApi } from '../../constants/passwordPolicy';
import { grecaptcha } from '../../helpers/grecaptcha';
import { Record } from '../../modules/bin/utility';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  useRecordOutcome,
  resolveSaveErrorKey
} from './useForcedPasswordReset';
import ValidatedInput from '../common/validatedInput';
import InvalidPassword from '../authentication/invalidPassword';
import SaveSecurityModal from '../../pages/accounts/settings/MFA/saveSecurityModal';

/**
 * The forced-reset wall. Not dismissable: the API refuses everything outside the calls that satisfy
 * the rotation, so there is nothing behind this modal to go back to. Log out is the only other way
 * out, matching how the MFA enrolment wall behaves.
 */
const ForcePasswordResetModal = () => {
  const dispatch = useAppDispatch();

  // One selector per slice, destructured. Selecting the slice itself is reference-stable, so this
  // re-renders no more often than a field-by-field version would, and adding a field later is a
  // line in the pattern rather than another hook.
  const {
    id: accountId,
    hasMfaActive,
    passwordPolicy: reportedPolicy
  } = useAppSelector((state) => get(state.session, 'account')) ?? {};

  const {
    newPassword = '',
    confirmPassword = '',
    recordChangePassword: record,
    accountVerification: { open: verificationOpen } = {}
  } = useAppSelector((state: any) => state.accountSettings) ?? {};

  // Derived here rather than inside the selector: getPolicyFromApi returns a new object, so
  // selecting its result would hand useSelector a fresh reference on every dispatch and re-render
  // the modal on every unrelated action. The API reports the policy for this account on the
  // session, so the numbers are never duplicated here, and an older API without it falls back to
  // the standard rule.
  const passwordPolicy = useMemo(
    () => getPolicyFromApi(reportedPolicy),
    [reportedPolicy]
  );

  const save = useCallback(
    (verificationVariant: any = null, verificationCode: any = null) => {
      dispatch(
        updateRecordPassword(
          accountId,
          { password: newPassword },
          verificationVariant,
          verificationCode
        )
      );
    },
    [dispatch, accountId, newPassword]
  );

  const onFailure = useCallback(() => {
    // The verification modal sits on top of this one, so a failed save leaves its own error
    // underneath and unreadable: the user is told nothing and re-enters the same code. The record
    // is deliberately left alone so the error survives to be shown.
    if (verificationOpen) {
      dispatch(verificationModalClear());
    }
  }, [dispatch, verificationOpen]);

  const onSuccess = useCallback(() => {
    if (verificationOpen) {
      dispatch(verificationModalClear());
    }

    // This wall is open because the session says a rotation is outstanding, and the session does
    // not know the save just satisfied it. Without a refresh the API has cleared the flag while
    // the dashboard still shows the wall, which reads as the save having done nothing. Ping is
    // inside the walled garden's allow-list, so it answers even while confined.
    grecaptcha((token?: string) => {
      dispatch(pingSession(token ?? null));
    }, 'SessionOperation');
  }, [dispatch, verificationOpen]);

  useRecordOutcome(record, { onSuccess, onFailure });

  const onSaveClick = () => {
    // An enrolled account has to verify before any account write, so going straight to the save
    // would 401. Same reason updatePassword pops this modal first.
    if (hasMfaActive) {
      dispatch(handleValueChange('accountVerification.open', true));
    } else {
      save();
    }
  };

  const onLogOutClick = () => {
    grecaptcha((token?: string) => {
      dispatch(logOut(token ?? null));
    }, 'SessionOperation');
    // The confined session has already had calls refused, so reload rather than trust the page.
    setTimeout(() => window.location.reload(), 2000);
  };

  const passwordErrors = getPasswordErrorMessage(newPassword, passwordPolicy);
  const isLoading = Record.isRecordLoading(record);
  const isSaveDisabled =
    passwordErrors !== undefined || newPassword !== confirmPassword;

  return (
    <Modal size="tiny" dimmer="inverted" open closeOnDimmerClick={false}>
      <Modal.Header>
        <h3>
          <Translate value="account.force-password-reset.title" />
        </h3>
      </Modal.Header>
      <Modal.Content>
        {/* A plain Message, not RecordResultMessage: that one is position:absolute;top:0 with a
            z-index, so inside a modal it lands on the header instead of above the form. */}
        {Record.isError(record) && (
          <Message
            negative
            size="small"
            onDismiss={() => dispatch(clearRecordChangePasswordMessages())}
          >
            <Message.Content>
              {I18n.t(resolveSaveErrorKey(get(record, 'error.errorKeys')))}
            </Message.Content>
          </Message>
        )}
        <p>
          <Translate value="account.force-password-reset.body" />
        </p>
        <Form>
          <Form.Field>
            <ValidatedInput
              value={newPassword}
              label={I18n.t('account.force-password-reset.new-password')}
              placeholder={I18n.t('account.force-password-reset.new-password')}
              type="password"
              onChange={(_event: any, { value }: any) =>
                dispatch(newPasswordChange(value))
              }
              error={passwordErrors !== undefined}
              errorComponent={<InvalidPassword errors={passwordErrors} />}
            />
          </Form.Field>
          <Form.Field>
            <ValidatedInput
              value={confirmPassword}
              label={I18n.t('account.force-password-reset.confirm-password')}
              placeholder={I18n.t(
                'account.force-password-reset.confirm-password'
              )}
              type="password"
              onChange={(_event: any, { value }: any) =>
                dispatch(confirmPasswordChange(value))
              }
              errorMessage={
                newPassword !== confirmPassword
                  ? I18n.t('errors.validation.password.not-equal')
                  : ''
              }
            />
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onLogOutClick}>{I18n.t('general.log-out')}</Button>
        <Button
          primary
          disabled={isSaveDisabled}
          loading={isLoading}
          onClick={onSaveClick}
        >
          <Translate value="account.force-password-reset.save" />
        </Button>
      </Modal.Actions>
      <SaveSecurityModal isLoading={isLoading} handleVerificationClick={save} />
    </Modal>
  );
};

export default ForcePasswordResetModal;
